import { parseCollis, computeDifference } from "converterAlgorithm";
import { showToast, hideToast } from "converterUtils";

/*
  FLEXA Converter - main element and calculation orchestration. FULL FILE REPLACEMENT.

  CHANGES IN THIS VERSION
  -----------------------
  1. prepareProducts now flags every product with isPart. The worker uses that to
     keep complete beds out of the surplus pass, so the tool can never answer
     "buy this other bed" as though it were a spare part.

  2. When the chosen solution carries parts the customer does not need (a kit that
     also contains drawers they already own), those parts are looked up and pushed
     into the excess list, so the summary shows them as spares instead of hiding
     the fact that they are being paid for.

  3. The whole-bed fallback now sets quantity: 1. It was previously undefined,
     which meant the cart add for that path had no quantity.

  4. prepareProducts no longer reads product.product.variants[0].sku, which does
     not exist in the converter JSON and would have thrown if it ever ran.
*/

class ConversionCalculator {
  constructor(workerUrl) {
    this.worker = new Worker(workerUrl);
    this.workerPromises = new Map();
    this.setupWorker();
  }

  setupWorker() {
    this.worker.onmessage = (event) => {
      const { requestId, ...result } = event.data;
      const resolve = this.workerPromises.get(requestId);
      if (resolve) {
        resolve(result);
        this.workerPromises.delete(requestId);
      }
    };

    this.worker.onerror = (error) => {
      console.error("Worker error:", error);
    };
  }

  async calculateProductDifferences(currentId, upgradeId, selectedCollection, partProducts) {
    const currentProduct = this.findProductById(currentId, selectedCollection.products);
    const upgradeProduct = this.findProductById(upgradeId, selectedCollection.products);

    const currentCollis = parseCollis(currentProduct.collis);
    const upgradeCollis = parseCollis(upgradeProduct.collis);
    const difference = computeDifference(currentCollis, upgradeCollis);

    const adjustedCurrentCollis = { ...currentCollis };
    Object.entries(difference.extra).forEach(([sku, qty]) => {
      if (adjustedCurrentCollis[sku]) {
        adjustedCurrentCollis[sku] -= qty;
        if (adjustedCurrentCollis[sku] <= 0) delete adjustedCurrentCollis[sku];
      }
    });

    // Collection products are complete beds, partProducts are the sellable parts.
    // The flag travels through to the worker.
    const allProducts = [
      ...this.prepareProducts(selectedCollection.products, false),
      ...this.prepareProducts(partProducts, true),
    ];

    return this.computeResults(
      difference,
      allProducts,
      currentCollis,
      adjustedCurrentCollis,
      selectedCollection.products,
      upgradeId
    );
  }

  async computeResults(
    difference,
    allProducts,
    currentCollis,
    adjustedCurrentCollis,
    products,
    upgradeId
  ) {
    const toastId = showToast(window.theme.converterStrings.toast.calculating, { type: "info" });

    const calculate = (diff, productList, collis) => {
      return new Promise((resolve) => {
        const requestId = `${Date.now()}-${Math.random()}`;
        this.workerPromises.set(requestId, resolve);
        this.worker.postMessage({ diff, products: productList, currentProductCollis: collis, requestId });
      });
    };

    try {
      const results = await Promise.all([
        calculate(difference.diff, allProducts, currentCollis).then((matchingSellableProducts) => ({
          matchingSellableProducts,
        })),
        calculate(adjustedCurrentCollis, allProducts).then((matchingCurrentProducts) => ({
          matchingCurrentProducts,
        })),
        calculate(difference.extra, allProducts).then((matchingExcessProducts) => ({
          matchingExcessProducts,
        })),
      ]);

      const result = Object.assign({}, ...results);

      // If the solution includes parts the customer did not ask for, name them and
      // add them to the excess list so they show up as spares in the summary.
      const surplus = result.matchingSellableProducts.surplus;
      if (surplus && Object.keys(surplus).length > 0) {
        const surplusMatch = await calculate(surplus, allProducts);
        const alreadyListed = new Set(result.matchingExcessProducts.products.map((p) => p.id));
        surplusMatch.products.forEach((p) => {
          if (!alreadyListed.has(p.id)) result.matchingExcessProducts.products.push(p);
        });
      }

      hideToast(toastId);

      if (this.handleNoMatchingSellableProducts(result, products, upgradeId)) {
        // Toast already shown in handleNoMatchingSellableProducts
      } else {
        showToast(window.theme.converterStrings.toast.finished, { duration: 2000, type: "success" });
      }

      return result;
    } catch (error) {
      hideToast(toastId);
      showToast(window.theme.converterStrings.toast.failed, { duration: 3000, type: "warning" });
      throw error;
    }
  }

  findProductById(productId, products) {
    return products.find((p) => p.product.id == productId);
  }

  prepareProducts(products, isPart) {
    return products.map((product) => ({
      ...product,
      isPart: isPart === true,
      collis: product.collis || String(product.product.variantId || ""),
    }));
  }

  handleNoMatchingSellableProducts(result, products, upgradeId) {
    if (result.matchingSellableProducts.products.length === 0) {
      const upgradeProduct = products.find((p) => p.product.id == upgradeId);
      const mappedProduct = {
        id: upgradeProduct.product.id,
        handle: upgradeProduct.product.handle,
        variantId: upgradeProduct.product.variantId,
        quantity: 1,
        collis: parseCollis(upgradeProduct.collis),
      };

      result.matchingSellableProducts.products.push(mappedProduct);

      showToast(window.theme.converterStrings.toast.noMatch, {
        duration: 10000,
        type: "info",
        dismissable: true,
      });
      return true;
    }
    return false;
  }
}

class FlexaConverter extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeData();
    this.initializeDomElements();
    this.calculator = new ConversionCalculator(this.dataset.workerUrl);
    this.setupEventListeners();
  }

  initializeData() {
    this.collectionsData = this.parseJsonFromSelector("[data-collections]");
    this.partProducts = this.parseJsonFromSelector("[data-parts]");
    this.selectedCollection = null;
  }

  initializeDomElements() {
    this.domElements = {
      collections: this.querySelectorAll("[data-collection]"),
    };
  }

  setupEventListeners() {
    this.preventTriggerDefault();
    this.addCollectionClickListeners();
    this.addProductSelectionListener();
  }

  parseJsonFromSelector(selector) {
    return JSON.parse(this.querySelector(selector).innerText);
  }

  preventTriggerDefault() {
    this.querySelectorAll("gtc-trigger").forEach((trigger) => {
      trigger.addEventListener("click", (event) => event.preventDefault());
    });
  }

  addCollectionClickListeners() {
    this.domElements.collections.forEach((collection) => {
      collection.addEventListener("click", (event) => {
        if (event.target.closest("gtc-trigger")) return;
        const collectionIndex = event.target.closest("[data-collection]").dataset.collection;
        this.handleCollectionSelection(collectionIndex);
      });
    });
  }

  addProductSelectionListener() {
    document.addEventListener("product-selected", (event) => {
      const { role } = event.detail;
      const hasUpgrade = sessionStorage.getItem("selected-upgrade-id");

      if ((role === "current" && hasUpgrade) || role === "upgrade") {
        setTimeout(() => this.computeConversion(), 100);
      }
    });
  }

  handleCollectionSelection(collectionIndex) {
    this.selectedCollection = this.collectionsData[collectionIndex];
    sessionStorage.removeItem("selected-current-id");
    sessionStorage.removeItem("selected-upgrade-id");

    this.dispatchEvent("collection-selected", this.selectedCollection);
  }

  async computeConversion() {
    document.dispatchEvent(new Event("calculating-products"));

    const currentId = sessionStorage.getItem("selected-current-id");
    const upgradeId = sessionStorage.getItem("selected-upgrade-id");

    if (!this.isValidSelection(currentId, upgradeId)) return;

    const calculation = await this.calculator.calculateProductDifferences(
      currentId,
      upgradeId,
      this.selectedCollection,
      this.partProducts
    );
    this.dispatchCalculationResults(calculation);
  }

  isValidSelection(currentId, upgradeId) {
    return currentId && upgradeId;
  }

  dispatchEvent(eventName, detail) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  dispatchCalculationResults(calculation) {
    this.dispatchEvent("converter-calculated", calculation);
  }
}

if (!customElements.get("flexa-converter")) {
  customElements.define("flexa-converter", FlexaConverter);
}
